import React from 'react';
import { useWorkerStatus, useManualSync } from '../../hooks/useWorkerSync';
import { useToast } from '@/components/ui/feedback/Toast';
import styles from './AdminLayout.module.css';

const AdminDataSync: React.FC = () => {
  const { data: status, isLoading: statusLoading, error: statusError } = useWorkerStatus();
  const { showToast } = useToast();
  
  const {
    syncAll,
    syncLaunches,
    syncNews,
    syncSolarFlares,
    syncHuntsvilleEvents,
  } = useManualSync();

  const handleSyncAll = () => {
    syncAll.mutate(undefined, {
      onSuccess: (result) => {
        showToast(`Full sync completed - ${result.count || 0} items synced`, 'success', 5000);
      },
      onError: (error) => {
        showToast(`Sync failed: ${error.message}`, 'error', 5000);
      },
    });
  };

  const handleSyncLaunches = () => {
    syncLaunches.mutate(undefined, {
      onSuccess: (result) => {
        showToast(`Launches synced - ${result.count || 0} items synced`, 'success', 5000);
      },
      onError: (error) => {
        showToast(`Sync failed: ${error.message}`, 'error', 5000);
      },
    });
  };

  const handleSyncNews = () => {
    syncNews.mutate(undefined, {
      onSuccess: (result) => {
        showToast(`News synced - ${result.count || 0} items synced`, 'success', 5000);
      },
      onError: (error) => {
        showToast(`Sync failed: ${error.message}`, 'error', 5000);
      },
    });
  };

  const handleSyncSolarFlares = () => {
    syncSolarFlares.mutate(undefined, {
      onSuccess: (result) => {
        showToast(`Solar flares synced - ${result.count || 0} items synced`, 'success', 5000);
      },
      onError: (error) => {
        showToast(`Sync failed: ${error.message}`, 'error', 5000);
      },
    });
  };

  const handleSyncHuntsvilleEvents = () => {
    syncHuntsvilleEvents.mutate(undefined, {
      onSuccess: (result) => {
        showToast(`Local events synced - ${result.count || 0} items synced`, 'success', 5000);
      },
      onError: (error) => {
        showToast(`Sync failed: ${error.message}`, 'error', 5000);
      },
    });
  };

  if (statusLoading) {
    return (
      <div>
        <h2>Data Sync Management</h2>
        <p>⏳ Loading worker status...</p>
      </div>
    );
  }

  if (statusError) {
    return (
      <div>
        <h2>Huntsville STEM Events & Data Sync</h2>
        
        <div className={styles.errorAlert}>
          <h3 className={styles.errorTitle}>⚠️ Worker Offline</h3>
          <p className={styles.errorText}>The data sync worker is currently unavailable. Manual sync operations will not work until the worker is deployed and running.</p>
          <p className={styles.errorDetails}>
            Error: {statusError.message}
          </p>
        </div>
        
        <div className={styles.errorGrid}>
          <div className={styles.card}>
            <h3>Worker Status</h3>
            <div className={styles.statusGrid}>
              <div className={styles.statusRow}>
                <span>Status:</span>
                <span className={styles.badgeOffline}>
                  Offline
                </span>
              </div>
              <div className={styles.statusRow}>
                <span>Last Check:</span>
                <span className={styles.muted}>
                  {new Date().toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className={styles.card}>
            <h3>Deployment Info</h3>
            <p className={styles.deploymentText}>
              To deploy the worker, run:
            </p>
            <code className={styles.codeBlock}>
              ./scripts/deploy-worker.sh
            </code>
          </div>
        </div>

        <div className={styles.card}>
          <h3>Manual Sync (Unavailable)</h3>
          <div className={styles.disabledButtonGrid}>
            {['Sync All Data', 'Sync Launches', 'Sync HSV Events'].map((label) => (
              <button
                key={label}
                disabled
                className={styles.disabledButton}
              >
                {label}
              </button>
            ))}
          </div>
          <p className={styles.disabledText}>
            Sync buttons will be available once the worker is online.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2>Huntsville STEM Events & Data Sync</h2>
      
      {/* Worker Status */}
      <div className={styles.card}>
        <h3>Worker Status</h3>
        <div className={styles.grid}>
          <div className={styles.dataRow}>
            <span>Status:</span>
            <span className={`${styles.badge} ${
              status?.status === 'healthy' 
                ? styles.badgeSuccess
                : styles.badgeWarning
            }`}>
              {status?.status || 'Unknown'}
            </span>
          </div>
          <div className={styles.dataRow}>
            <span>Last Check:</span>
            <span className={styles.muted}>
              {status?.timestamp ? new Date(status.timestamp).toLocaleString() : 'Unknown'}
            </span>
          </div>
        </div>

        {status?.lastSync && (
          <div className={styles.section}>
            <h4>Last Sync Times:</h4>
            <div className={styles.syncGrid}>
              {status.lastSync.launches && (
                <div className={styles.syncItem}>
                  <span>Launches:</span>
                  <div className={styles.muted}>{new Date(status.lastSync.launches).toLocaleString()}</div>
                </div>
              )}
              {status.lastSync.news && (
                <div className={styles.syncItem}>
                  <span>News:</span>
                  <div className={styles.muted}>{new Date(status.lastSync.news).toLocaleString()}</div>
                </div>
              )}
              {status.lastSync.solarFlares && (
                <div className={styles.syncItem}>
                  <span>Solar Flares:</span>
                  <div className={styles.muted}>{new Date(status.lastSync.solarFlares).toLocaleString()}</div>
                </div>
              )}
              {status.lastSync.huntsvilleEvents && (
                <div className={styles.syncItem}>
                  <span>Local Events:</span>
                  <div className={styles.muted}>{new Date(status.lastSync.huntsvilleEvents).toLocaleString()}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Manual Sync Controls */}
      <div className={styles.card}>
        <h3>Manual Sync</h3>
        <p className={styles.description}>
          The worker automatically syncs data every 6 hours. Use these buttons for immediate updates:
        </p>

        <div className={styles.buttonGrid}>
          <button
            onClick={handleSyncAll}
            disabled={syncAll.isPending}
            className={`${styles.button} ${styles.buttonPrimary}`}
          >
            {syncAll.isPending ? 'Syncing All...' : 'Sync All Data'}
          </button>

          <button
            onClick={handleSyncLaunches}
            disabled={syncLaunches.isPending}
            className={`${styles.button} ${styles.buttonSecondary}`}
          >
            {syncLaunches.isPending ? 'Syncing...' : 'Sync Launches'}
          </button>

          <button
            onClick={handleSyncNews}
            disabled={syncNews.isPending}
            className={`${styles.button} ${styles.buttonSecondary}`}
          >
            {syncNews.isPending ? 'Syncing...' : 'Sync News'}
          </button>

          <button
            onClick={handleSyncSolarFlares}
            disabled={syncSolarFlares.isPending}
            className={`${styles.button} ${styles.buttonSecondary}`}
          >
            {syncSolarFlares.isPending ? 'Syncing...' : 'Sync Solar Flares'}
          </button>

          <button
            onClick={handleSyncHuntsvilleEvents}
            disabled={syncHuntsvilleEvents.isPending}
            className={`${styles.button} ${styles.buttonSecondary}`}
          >
            {syncHuntsvilleEvents.isPending ? 'Syncing...' : 'Sync Local Events'}
          </button>
        </div>

        <div className={styles.infoBox}>
          <h4>Automatic Sync Schedule</h4>
          <p>
            The Cloudflare Worker automatically syncs all data sources every 6 hours. 
            Manual syncs are useful for immediate updates after major events or when testing new features.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminDataSync;
